import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import { 
  Add, 
  Visibility as VisibilityIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  History as HistoryIcon,
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import apiClient from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

type WorkflowStatus = 'DRAFT' | 'PENDING_MANAGER_APPROVAL' | 'PENDING_LEGAL' | 'PENDING_AP' | 'COMPLETED' | 'REJECTED';

interface VendorRequest {
  id: string;
  companyName: string;
  requestorName: string;
  requestDate: string;
  status: WorkflowStatus;
  estimatedAmount: number;
  active: boolean;
  rejectionReason?: string;
}

const VendorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState<VendorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [auditData, setAuditData] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [showRemoved]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/api/vendors', {
        params: { includeInactive: showRemoved }
      });
      const submissions = response.data.data || [];
      
      // Map database fields to dashboard format
      const mapped = submissions.map((sub: any) => ({
        id: String(sub.id),
        companyName: sub.company_name || 'N/A',
        requestorName: sub.requestor_name || 'Unknown',
        requestDate: sub.request_date || sub.created_at,
        status: sub.workflow_status as WorkflowStatus,
        estimatedAmount: parseFloat(sub.estimated_contract_amount) || 0,
        active: sub.active !== false,
        rejectionReason: sub.rejection_reason || undefined,
      }));
      
      // Sort by request date descending (newest first)
      mapped.sort((a: VendorRequest, b: VendorRequest) => {
        const dateA = new Date(a.requestDate).getTime();
        const dateB = new Date(b.requestDate).getTime();
        return dateB - dateA;
      });
      
      setRequests(mapped);
    } catch (error: any) {
      console.error('Error loading requests:', error);
      if (error.response?.status === 401) {
        setError('Authentication required. Please log in.');
      } else {
        setError(error.message || 'Failed to load requests');
      }
      setRequests([]); // Set empty array to stop any retry attempts
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: WorkflowStatus) => {
    const statusConfig: Record<WorkflowStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
      DRAFT: { label: 'Draft', color: 'default' },
      PENDING_MANAGER_APPROVAL: { label: 'Pending Manager Approval', color: 'warning' },
      PENDING_LEGAL: { label: 'Pending Legal', color: 'info' },
      PENDING_AP: { label: 'Pending AP', color: 'primary' },
      COMPLETED: { label: 'Completed', color: 'success' },
      REJECTED: { label: 'Rejected', color: 'error' },
    };
    const config = statusConfig[status];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleViewEdit = (id: string) => {
    navigate(`/form/${id}`);
  };

  const handleCreateNew = () => {
    navigate('/form/new');
  };

  const handleDeleteClick = (id: string, companyName: string) => {
    setDeleteTarget({ id, name: companyName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    
    try {
      await apiClient.delete(`/api/vendors/${deleteTarget.id}`);
      setRequests(requests.filter(r => r.id !== deleteTarget.id));
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting submission:', error);
      setError('Failed to delete submission');
    }
  };

  const handleAuditClick = async (id: string) => {
    setAuditLoading(true);
    setAuditDialogOpen(true);
    
    try {
      const response = await apiClient.get(`/api/vendors/${id}/audit`);
      setAuditData(response.data.data || []);
    } catch (error) {
      console.error('Error loading audit trail:', error);
      setAuditData([]);
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Supplier Due Diligence Requests
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          size="large"
          onClick={handleCreateNew}
        >
          Create New Request
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isAdmin && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showRemoved}
                onChange={(e) => setShowRemoved(e.target.checked)}
                color="primary"
              />
            }
            label="Show removed items"
          />
        </Box>
      )}

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Company Name</strong></TableCell>
              <TableCell>Requestor</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Estimated Amount</TableCell>
              <TableCell align="right">Actions</TableCell>
              {isAdmin && <TableCell align="right">Admin</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No requests found. Click "Create New Request" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow
                  key={request.id}
                  sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                >
                  <TableCell>{request.id}</TableCell>
                  <TableCell>{request.companyName}</TableCell>
                  <TableCell>{request.requestorName}</TableCell>
                  <TableCell>{formatDate(request.requestDate)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusChip(request.status)}
                      {request.status === 'REJECTED' && request.rejectionReason && (
                        <Tooltip title={request.rejectionReason} arrow placement="top">
                          <InfoOutlinedIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(request.estimatedAmount)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleViewEdit(request.id)}
                      title={request.status === 'DRAFT' || request.status === 'REJECTED' ? 'Edit' : 'View'}
                    >
                      {request.status === 'DRAFT' || request.status === 'REJECTED' ? (
                        <EditIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleAuditClick(request.id)}
                        title="View Audit Trail"
                      >
                        <HistoryIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(request.id, request.companyName)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>No</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog 
        open={auditDialogOpen} 
        onClose={() => setAuditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Audit Trail History</DialogTitle>
        <DialogContent>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : auditData.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Notes/Rejection Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditData.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(entry.created_at).toLocaleString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entry.action_type} 
                          size="small"
                          color={entry.action_type === 'REJECTED' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{entry.workflow_stage}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {entry.user_name || entry.user_email || entry.user_id}
                        {entry.user_email && entry.user_name && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {entry.user_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{entry.rejection_reason || entry.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No audit trail data available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorDashboard;
